import type { JiraIssueCard, JiraPerson, JiraStatusCategory } from '@novachat/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightLeft, ExternalLink, Link2, MessageSquare, MoreHorizontal, UserMinus, UserPlus, UserRound } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { client } from '../lib/client';
import { useDismiss } from '../lib/dismiss';
import { errorMessage } from '../lib/errors';
import { Avatar } from './Avatar';
import { useJira } from './JiraSettings';
import { useSettings } from './SettingsDialog';

const LOZENGE: Record<JiraStatusCategory, string> = {
  todo: 'bg-surface-3 text-fg-2',
  doing: 'bg-indigo-400/25 text-indigo-200',
  done: 'bg-green-400/15 text-green-400',
};

export function StatusLozenge({ status }: { status: JiraIssueCard['status'] }) {
  return <span className={`inline-flex h-5 items-center rounded-xs px-1.5 text-[11px] font-bold tracking-[0.04em] uppercase ${LOZENGE[status.category]}`}>{status.name}</span>;
}

function Person({ person, size = 18 }: { person: Pick<JiraPerson, 'name' | 'userId'> & { accountId: string }; size?: number }) {
  return person.userId ? (
    <Avatar id={person.userId} name={person.name} size={size} />
  ) : (
    <span aria-hidden="true" style={{ width: size, height: size }} className="grid flex-none place-items-center rounded-full bg-surface-3 text-[9px] font-bold text-fg-2">
      {person.name.slice(0, 1).toLocaleUpperCase('tr')}
    </span>
  );
}

/** Jira issues the message names (docs/plans/2026-11-04-jira-design.md §2); `jira.issue` keeps them current. */
export function JiraCards({ cards }: { cards: readonly JiraIssueCard[] | undefined }) {
  if (!cards || cards.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      {cards.map((card) => (
        <JiraCard key={card.key} card={card} />
      ))}
    </div>
  );
}

type Panel = 'menu' | 'status' | 'assign' | 'comment';

function JiraCard({ card }: { card: JiraIssueCard }) {
  const [panel, setPanel] = useState<Panel | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(panel !== null, () => setPanel(null), box, button);

  return (
    <div className="relative max-w-[460px] rounded-md border border-l-[3px] border-line border-l-[#1868DB] bg-surface-2 p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5 text-xs text-fg-3">
            <span className="flex-none font-bold text-fg-2">{card.key}</span>
            <span aria-hidden="true">·</span>
            <span className="truncate">{[card.type, card.project].filter(Boolean).join(' · ')}</span>
          </div>
          <a href={card.url} target="_blank" rel="noopener noreferrer" className="mt-0.5 block text-sm font-semibold text-fg-1 [overflow-wrap:anywhere] hover:underline">
            {card.summary}
          </a>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-fg-3">
            <StatusLozenge status={card.status} />
            {card.priority && <span>Öncelik: {card.priority}</span>}
            {card.assignee ? (
              <span className="flex min-w-0 items-center gap-1.5">
                <Person person={card.assignee} />
                <span className="truncate text-fg-2">{card.assignee.name}</span>
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <UserRound size={13} aria-hidden="true" /> Atanmamış
              </span>
            )}
          </div>
        </div>
        <button
          ref={button}
          type="button"
          aria-label={`${card.key} işlemleri`}
          aria-expanded={panel !== null}
          onClick={() => setPanel((p) => (p ? null : 'menu'))}
          className="nc-press grid size-8 flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-3 hover:text-fg-1"
        >
          <MoreHorizontal size={16} />
        </button>
      </div>
      {panel && (
        <div ref={box} className="absolute top-11 right-2 z-20 flex w-[min(272px,calc(100%-16px))] animate-enter flex-col rounded-md border border-line-strong bg-surface-2 p-1 shadow-2xl">
          {panel === 'menu' ? (
            <Menu card={card} open={setPanel} />
          ) : panel === 'status' ? (
            <StatusPanel card={card} done={() => setPanel(null)} />
          ) : panel === 'assign' ? (
            <AssignPanel card={card} done={() => setPanel(null)} />
          ) : (
            <CommentPanel card={card} done={() => setPanel(null)} />
          )}
        </div>
      )}
    </div>
  );
}

function Item({ icon, label, onClick, href, disabled }: { icon: ReactNode; label: string; onClick?: () => void; href?: string; disabled?: boolean }) {
  const look = 'flex items-center gap-2.5 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left text-[13.5px] text-fg-1 no-underline hover:bg-surface-3 disabled:opacity-50';
  return href ? (
    <a role="menuitem" href={href} target="_blank" rel="noopener noreferrer" className={look}>
      {icon}
      {label}
    </a>
  ) : (
    <button type="button" role="menuitem" disabled={disabled} onClick={onClick} className={look}>
      {icon}
      {label}
    </button>
  );
}

function Menu({ card, open }: { card: JiraIssueCard; open: (panel: Panel | null) => void }) {
  const jira = useJira();
  const me = jira.data?.connection ?? null;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function assignToMe() {
    if (!me) return;
    setBusy(true);
    setError(null);
    try {
      await client.api.jiraAssign(card.key, me.accountId);
      open(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div role="menu" aria-label={`${card.key} işlemleri`} className="flex flex-col">
      {me ? (
        <>
          <Item icon={<ArrowRightLeft size={15} />} label="Durumu değiştir" onClick={() => open('status')} />
          {card.assignee?.accountId !== me.accountId && <Item icon={<UserPlus size={15} />} label="Bana ata" disabled={busy} onClick={() => void assignToMe()} />}
          <Item icon={<UserRound size={15} />} label="Başkasına ata" onClick={() => open('assign')} />
          <Item icon={<MessageSquare size={15} />} label="Yorum yaz" onClick={() => open('comment')} />
        </>
      ) : (
        jira.data?.available && (
          <Item
            icon={<Link2 size={15} />}
            label="Jira hesabını bağla"
            onClick={() => {
              open(null);
              useSettings.getState().show('connections');
            }}
          />
        )
      )}
      <Item icon={<ExternalLink size={15} />} label="Jira’da aç" href={card.url} />
      {error && (
        <p role="alert" className="m-0 px-2.5 py-1.5 text-[12.5px] text-[#EE9191]">
          {error}
        </p>
      )}
    </div>
  );
}

function PanelTitle({ children }: { children: ReactNode }) {
  return <p className="m-0 px-2.5 pt-1.5 pb-1 text-[11px] font-bold tracking-[0.06em] text-fg-3 uppercase">{children}</p>;
}

function StatusPanel({ card, done }: { card: JiraIssueCard; done: () => void }) {
  const transitions = useQuery({ queryKey: ['jira-transitions', card.key], queryFn: () => client.api.jiraTransitions(card.key), staleTime: 0 });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function move(id: string) {
    setBusy(true);
    setError(null);
    try {
      await client.api.jiraTransition(card.key, id);
      done();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <div role="menu" aria-label="Durumu değiştir" className="flex flex-col">
      <PanelTitle>Durumu değiştir</PanelTitle>
      {transitions.isPending ? (
        <p className="m-0 px-2.5 py-2 text-[13px] text-fg-3">Yükleniyor…</p>
      ) : !transitions.data ? (
        <p className="m-0 px-2.5 py-2 text-[13px] text-[#EE9191]">{errorMessage(transitions.error)}</p>
      ) : (
        transitions.data.map((t) => (
          <button
            key={t.id}
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => void move(t.id)}
            className="flex items-center gap-2 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left hover:bg-surface-3 disabled:opacity-50"
          >
            <StatusLozenge status={t.to} />
            {t.name !== t.to.name && <span className="truncate text-[13px] text-fg-2">{t.name}</span>}
          </button>
        ))
      )}
      {error && (
        <p role="alert" className="m-0 px-2.5 py-1.5 text-[12.5px] text-[#EE9191]">
          {error}
        </p>
      )}
    </div>
  );
}

function AssignPanel({ card, done }: { card: JiraIssueCard; done: () => void }) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);
  const people = useQuery({ queryKey: ['jira-assignable', card.key, debounced], queryFn: () => client.api.jiraAssignable(card.key, debounced), staleTime: 60_000 });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function assign(accountId: string | null) {
    setBusy(true);
    setError(null);
    try {
      await client.api.jiraAssign(card.key, accountId);
      done();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PanelTitle>Ata</PanelTitle>
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Kişi ara"
        aria-label="Atanacak kişiyi ara"
        className="mx-1 mb-1 h-9 rounded-sm border border-line-strong bg-surface-1 px-2.5 text-base text-fg-1 outline-none focus:border-brand-500 sm:text-sm"
      />
      <div role="menu" aria-label="Atanabilecek kişiler" className="flex max-h-60 flex-col overflow-y-auto">
        {card.assignee && <Item icon={<UserMinus size={15} />} label="Atamayı kaldır" disabled={busy} onClick={() => void assign(null)} />}
        {people.isPending ? (
          <p className="m-0 px-2.5 py-2 text-[13px] text-fg-3">Yükleniyor…</p>
        ) : !people.data ? (
          <p className="m-0 px-2.5 py-2 text-[13px] text-[#EE9191]">{errorMessage(people.error)}</p>
        ) : people.data.length === 0 ? (
          <p className="m-0 px-2.5 py-2 text-[13px] text-fg-3">Kimse bulunamadı.</p>
        ) : (
          people.data.map((person) => (
            <button
              key={person.accountId}
              type="button"
              role="menuitem"
              disabled={busy}
              onClick={() => void assign(person.accountId)}
              className="flex items-center gap-2 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left text-[13.5px] text-fg-1 hover:bg-surface-3 disabled:opacity-50"
            >
              <Person person={person} size={20} />
              <span className="truncate">{person.name}</span>
            </button>
          ))
        )}
      </div>
      {error && (
        <p role="alert" className="m-0 px-2.5 py-1.5 text-[12.5px] text-[#EE9191]">
          {error}
        </p>
      )}
    </div>
  );
}

function CommentPanel({ card, done }: { card: JiraIssueCard; done: () => void }) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function send() {
    if (!text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await client.api.jiraComment(card.key, text);
      done();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-1.5 p-1">
      <PanelTitle>{card.key} için yorum</PanelTitle>
      <textarea
        autoFocus
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send();
        }}
        aria-label={`${card.key} için Jira yorumu`}
        placeholder="Jira’ya senin adınla gider"
        className="resize-none rounded-sm border border-line-strong bg-surface-1 px-2.5 py-2 text-base text-fg-1 outline-none focus:border-brand-500 sm:text-sm"
      />
      {error && (
        <p role="alert" className="m-0 text-[12.5px] text-[#EE9191]">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={done} className="nc-press h-8 rounded-sm border border-line-strong bg-surface-1 px-3 text-[13px] font-semibold text-fg-1">
          Vazgeç
        </button>
        <button type="button" disabled={busy || !text.trim()} onClick={() => void send()} className="nc-press h-8 rounded-sm border-0 bg-brand-500 px-3 text-[13px] font-bold text-white disabled:opacity-50">
          Gönder
        </button>
      </div>
    </div>
  );
}
